import styled, { css, useTheme } from "styled-components";
import Select, { SelectProps } from "common/Select/Select.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { BaseOptionType, DefaultOptionType, SelectValue } from "antd/es/select";
import { CSSProperties } from "react";
import { getNewLookChipColors } from "common/Filters/common/styles";

interface FiltersSelectProps<
  VT,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
> extends SelectProps<VT, OptionType> {
  $singleRow?: boolean;
  $width?: number;
  $allSelected?: boolean;
  $isBottomNavigator?: boolean;
  $allowClear?: boolean;
}

const FiltersSelect = <
  VT extends SelectValue = SelectValue,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
>({
  $singleRow,
  $allSelected,
  $width,
  $allowClear,
  $isBottomNavigator,
  ...props
}: FiltersSelectProps<VT, OptionType>) => {
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const changeActiveColor = isBottomNavigator && !$allSelected;
  const isMultiSelect = props.mode === "multiple";

  const rootStyle: CSSProperties = {
    background: changeActiveColor
      ? theme.colors.filterChipsActiveColor
      : theme.colors.filterChipsColor,
    borderRadius: 40,
    border: "none",
    boxShadow: "none",
    paddingInlineEnd: isBottomNavigator ? (isMultiSelect ? 0 : 10) : 24,
    color: changeActiveColor ? theme.colors.white : undefined,
    height: 32,
  };
  if (theme.isNewLook) {
    const chip = getNewLookChipColors(theme, changeActiveColor);
    rootStyle.background = chip.background;
    rootStyle.border = chip.border;
    rootStyle.color = chip.color;
    rootStyle.borderRadius = theme.radii.control;
    // antd gives multi-selects a 40px min-height on phones; keep the chip
    // the same 32px as the filter and sort buttons beside it.
    rootStyle.minHeight = 32;
  }
  if (!isMultiSelect) {
    rootStyle.paddingTop = 0;
    rootStyle.paddingBottom = 0;
  }
  let placeholderColor = changeActiveColor
    ? theme.colors.white
    : theme.colors.primaryText;
  if (theme.isNewLook)
    placeholderColor = getNewLookChipColors(theme, changeActiveColor).color;

  return (
    <StyledSelect
      {...props}
      data-chip-active={changeActiveColor || undefined}
      styles={
        $singleRow
          ? {
              root: rootStyle,
              item: isBottomNavigator
                ? {
                    background: "transparent",
                    border: "none",
                    maxWidth: "100%",
                    paddingInlineEnd: 0,
                    marginInlineEnd: 0,
                  }
                : { borderRadius: 10 },
              placeholder: {
                color: placeholderColor,
                ...(theme.isNewLook && { fontSize: 13 }),
              },
            }
          : undefined
      }
    />
  );
};
export default FiltersSelect;

type StyledSelectComponent = <
  VT extends SelectValue = SelectValue,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
>(
  props: SelectProps<VT, OptionType>
) => React.ReactElement;

const StyledSelect: StyledSelectComponent = styled(Select)`
  .ant-select-content {
    &:before {
      display: none;
    }
    flex-wrap: nowrap;
  }
  .ant-select-placeholder {
    overflow: visible;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      &&[data-chip-active] .ant-select-selection-item,
      &&[data-chip-active] .ant-select-selection-item-content {
        color: inherit;
      }
    `}
`;
